/* eslint-disable react/jsx-props-no-spreading */

import PropTypes from 'prop-types';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import {
  graphql,
  usePreloadedQuery,
} from 'react-relay';

import PlaceholderErrorBoundary from 'src/components/ErrorBoundary/PlaceholderErrorBoundary';
import Loading from 'src/components/Loading';
import { PropertySettings } from 'src/components/Property';
import PropertySettingsPlaceholder from 'src/components/Property/PropertySettings/Placeholder';

import useRouteQueryRetry from './helpers/useRouteQueryRetry';

const messages = defineMessages({
  initialLoadError: {
    id: 'property.property_settings.initial_load_error.message',
    defaultMessage: 'An error occurred while loading the property settings. Please try again.',
  },
});

export const propertySettingsRouteQuery = graphql`
  query PropertySettingsRoute_Query($uuid: ID!) {
    property(uuid: $uuid) {
      ...PropertySettings_property
    }
  }
`;

const propertySettingsRoutePropsShape = PropTypes.shape({
  match: PropTypes.shape({
    params: PropTypes.shape({
      uuid: PropTypes.string,
    }),
  }),
});

/**
 * Renders `PropertySettings` from a manually retried Relay query reference.
 * @param {object} props - Component props.
 * @param {object} props.routeProps - Found-relay route props (match/router/location).
 * @param {object} props.queryRef - Relay preloaded query reference created by `loadQuery`.
 * @returns {React.ReactNode} property settings route content.
 */
function PropertySettingsPreloadedQueryRenderer({ routeProps, queryRef }) {
  const queryData = usePreloadedQuery(propertySettingsRouteQuery, queryRef);
  return <PropertySettings {...routeProps} {...queryData} />;
}

PropertySettingsPreloadedQueryRenderer.propTypes = {
  routeProps: propertySettingsRoutePropsShape.isRequired,
  queryRef: PropTypes.shape({
    dispose: PropTypes.func,
  }).isRequired,
};

/**
 * Hook-safe renderer for the `PropertySettings` route.
 * Keeps the initial found-relay result for first paint, then switches to a
 * `useQueryLoader`-managed query reference when the user retries after an
 * error so Relay can suspend back to the loading state.
 * @param {object} props - Component props.
 * @param {object} props.routeProps - Original found-relay route props.
 * @returns {React.ReactNode} property settings route content.
 */
function PropertySettingsRouteRenderer({ routeProps }) {
  const intl = useIntl();
  const { queryRef, handleRetry, retryDisabled } = useRouteQueryRetry(
    propertySettingsRouteQuery,
    routeProps,
    { logLabel: 'PropertySettingsRoute' },
  );

  return (
    <PlaceholderErrorBoundary
      bodyText={intl.formatMessage(messages.initialLoadError)}
      placeholderComponent={PropertySettingsPlaceholder}
      onRetryFunc={handleRetry}
      retryDisabled={retryDisabled}
    >
      <React.Suspense fallback={<Loading />}>
        {queryRef ? (
          <PropertySettingsPreloadedQueryRenderer
            routeProps={routeProps}
            queryRef={queryRef}
          />
        ) : (
          <PropertySettings {...routeProps} />
        )}
      </React.Suspense>
    </PlaceholderErrorBoundary>
  );
}

PropertySettingsRouteRenderer.propTypes = {
  routeProps: propertySettingsRoutePropsShape.isRequired,
};

export default PropertySettingsRouteRenderer;
